import * as React from 'react'
// constants
import { ENTITIES, Modals } from 'constants/index'
// context
import { ReviewDrawerContext } from 'context/ReviewDrawerContext'
// hooks
import { useShowModal } from 'hooks/index'
// types
import { ISecurityProfilePermissions } from 'types/graphql'

type Props = React.PropsWithChildren<unknown>

interface IPlanItemClickData {
  id: string
  parentPermissions: ISecurityProfilePermissions
  // color the item is shown with in the tree
  color?: string
}

interface IContext {
  handleActionClick: (data: IPlanItemClickData) => () => Promise<void>
  handleKPIClick: (data: IPlanItemClickData) => () => Promise<void>
  handleObjectiveClick: (data: IPlanItemClickData) => () => Promise<void>
}

export const ObjectivesListNavigationBLContext = React.createContext<IContext>({} as any)

const ObjectivesListNavigationBL: React.FC<Props> = ({ children }) => {
  const isReview = React.useContext(ReviewDrawerContext)

  // redux
  const { hideModal: hideActionModal, showModal: showActionModal } = useShowModal({
    modalType: Modals.ACTION_MINIMIZED_MODAL
  })
  const { hideModal: hideKPIModal, showModal: showKPIModal } = useShowModal({ modalType: Modals.KPI_MINIMIZED_MODAL })
  const { hideModal: hideObjectiveModal, showModal: showObjectiveModal } = useShowModal({
    modalType: Modals.OBJECTIVE_MINIMIZED_MODAL
  })
  const { showModal: showPreviewModal } = useShowModal({ modalType: Modals.PLAN_ITEM_PREVIEW_MODAL })

  const showPreview = (id: string, entityType: string) => {
    showPreviewModal({ entity: { id, entityType } })
  }

  // methods
  const handleObjectiveClick =
    ({ id, parentPermissions, color }) =>
    async () => {
      if (isReview) {
        return showPreview(id, ENTITIES.PLAN_OBJECTIVE)
      }

      hideActionModal()
      hideKPIModal()
      showObjectiveModal({ id, parentPermissions, color })
    }

  const handleActionClick =
    ({ id, parentPermissions, color }) =>
    async () => {
      if (isReview) {
        return showPreview(id, ENTITIES.PLAN_ACTION)
      }

      hideKPIModal()
      hideObjectiveModal()
      showActionModal({ id, parentPermissions, color })
    }

  const handleKPIClick =
    ({ id, parentPermissions, color }) =>
    async () => {
      if (isReview) {
        return showPreview(id, ENTITIES.PLAN_KPI)
      }

      hideActionModal()
      hideObjectiveModal()
      showKPIModal({ id, parentPermissions, color })
    }

  const value = { handleActionClick, handleKPIClick, handleObjectiveClick }

  return (
    <ObjectivesListNavigationBLContext.Provider value={value}>{children}</ObjectivesListNavigationBLContext.Provider>
  )
}

export default ObjectivesListNavigationBL
